import { PaletteMode, ThemeOptions, alpha } from "@mui/material";

const expressivePalette = {
  light: {
    primary: "#6750A4",
    onPrimary: "#FFFFFF",
    primaryContainer: "#EADDFF",
    onPrimaryContainer: "#21005D",
    secondary: "#625B71",
    onSecondary: "#FFFFFF",
    secondaryContainer: "#E8DEF8",
    onSecondaryContainer: "#1D192B",
    tertiary: "#7D5260",
    onTertiary: "#FFFFFF",
    tertiaryContainer: "#FFD8E4",
    onTertiaryContainer: "#31111D",
    error: "#B3261E",
    onError: "#FFFFFF",
    errorContainer: "#F9DEDC",
    onErrorContainer: "#410E0B",
    background: "#FEF7FF",
    surface: "#FEF7FF",
    surfaceVariant: "#E7E0EB",
    outline: "#79747E",
    outlineVariant: "#CAC4D0",
    inverseSurface: "#322F35",
    inverseOnSurface: "#F5EEFA",
    inversePrimary: "#CFBCFF",
  },
  dark: {
    primary: "#CFBCFF",
    onPrimary: "#381E72",
    primaryContainer: "#4F378A",
    onPrimaryContainer: "#EADDFF",
    secondary: "#CBC2DB",
    onSecondary: "#332D41",
    secondaryContainer: "#4A4458",
    onSecondaryContainer: "#E8DEF8",
    tertiary: "#EFB8C8",
    onTertiary: "#492532",
    tertiaryContainer: "#633B48",
    onTertiaryContainer: "#FFD8E4",
    error: "#F2B8B5",
    onError: "#601410",
    errorContainer: "#8C1D18",
    onErrorContainer: "#F9DEDC",
    background: "#141218",
    surface: "#141218",
    surfaceVariant: "#49454F",
    outline: "#938F99",
    outlineVariant: "#49454F",
    inverseSurface: "#E6E0E9",
    inverseOnSurface: "#1D1B20",
    inversePrimary: "#6750A4",
  },
};

export const getExpressiveThemeOptions = (mode: PaletteMode): ThemeOptions => {
  const palette = expressivePalette[mode];

  const displayFont =
    '"Space Grotesk", "Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif';

  return {
    palette: {
      mode,
      primary: {
        main: palette.primary,
        contrastText: palette.onPrimary,
      },
      secondary: {
        main: palette.secondary,
        contrastText: palette.onSecondary,
      },
      error: {
        main: palette.error,
        contrastText: palette.onError,
      },
      background: {
        default: palette.background,
        paper: palette.surface,
      },
      text: {
        primary: mode === "light" ? "#1D1B20" : "#E6E0E9",
        secondary: mode === "light" ? "#49454F" : "#CAC4D0",
      },
      divider: alpha(palette.outlineVariant, mode === "light" ? 0.6 : 0.4),
    },
    shape: {
      borderRadius: 22,
    },
    typography: {
      fontFamily:
        '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily: displayFont,
        fontWeight: 700,
        letterSpacing: -0.015,
      },
      h2: {
        fontFamily: displayFont,
        fontWeight: 700,
        letterSpacing: -0.01,
      },
      h3: {
        fontFamily: displayFont,
        fontWeight: 600,
        letterSpacing: -0.005,
      },
      h4: {
        fontFamily: displayFont,
        fontWeight: 600,
      },
      h5: {
        fontFamily: displayFont,
        fontWeight: 600,
      },
      h6: {
        fontFamily: displayFont,
        fontWeight: 600,
        letterSpacing: 0.1,
      },
      subtitle1: {
        fontWeight: 500,
      },
      subtitle2: {
        fontWeight: 600,
        letterSpacing: 0.1,
      },
      button: {
        fontWeight: 600,
        letterSpacing: 0.1,
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background,
            backgroundImage:
              mode === "light"
                ? "radial-gradient(circle at 20% 20%, rgba(103, 80, 164, 0.08), transparent 45%), radial-gradient(circle at 80% 0%, rgba(125, 82, 96, 0.08), transparent 55%)"
                : "radial-gradient(circle at 10% 10%, rgba(207, 188, 255, 0.12), transparent 45%), radial-gradient(circle at 70% -10%, rgba(239, 184, 200, 0.12), transparent 55%)",
            transition: "background-color 0.3s ease, color 0.3s ease",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 24,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingLeft: 20,
            paddingRight: 20,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow:
              mode === "light"
                ? "0px 20px 40px rgba(103, 80, 164, 0.25)"
                : "0px 16px 32px rgba(0, 0, 0, 0.35)",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 18,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundImage: "none",
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: alpha(palette.surface, mode === "light" ? 0.8 : 0.6),
            backdropFilter: "blur(18px)",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 999,
          },
        },
      },
    },
  };
};

export default getExpressiveThemeOptions;
